import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { broadcastAccountData } from 'src/composables/app/system/useBroadcastChannel'
import { log } from 'src/services/app/log.service'
import { localStorageService } from 'src/services/app/storage.service'
import { notifyMsgs, notifyService } from 'src/services/app/notify.service'
import { ME_CACHE_TTL_MS, authService } from 'src/services/api/auth.service'
import { STORAGE_KEYS } from 'src/services/consts/api.const'
import { MUTATION_KEYS, QUERY_KEYS } from 'src/services/consts/queries.const'
import { ACCOUNT_ERROR_CODE } from 'src/services/consts/account.const'
import { wrapMutationAsync } from 'src/services/helpers/util.helper'

/**
 * Account update mutation for account settings.
 *
 * Persists via `authService.patchAccount` (PATCH /account), sets the
 * ACCOUNT_DATA query cache, syncs ME_ACCOUNT_CACHE, then broadcasts for
 * cross-tab sync.
 *
 * Consumed via `useAccountActionsStore` — components never call this
 * composable directly (project mutation convention).
 */
export function usePatchAccountMutation() {
  const queryClient = useQueryClient()

  const patchAccountMutation = useMutation({
    mutationKey: [MUTATION_KEYS.PATCH_ACCOUNT],
    mutationFn: ({ accountName, companyName, country }) =>
      authService.patchAccount({ accountName, companyName, country }),

    onSuccess: (accountData) => {
      queryClient.setQueryData([QUERY_KEYS.ACCOUNT_DATA], accountData)
      localStorageService.cacheData(STORAGE_KEYS.ME_ACCOUNT_CACHE, accountData, ME_CACHE_TTL_MS)
      broadcastAccountData(accountData)
      notifyService.success(notifyMsgs.success.patchAccount)
    },

    onError: (error) => {
      log.error('[Account] patchAccount failed', error)

      const code = error?.response?.data?.error?.code

      if (code === ACCOUNT_ERROR_CODE.ACCOUNT_NAME_TAKEN) {
        notifyService.error(notifyMsgs.error.accountNameTaken)
        return
      }

      notifyService.error(notifyMsgs.error.patchAccount)
    },
  })

  return {
    patchAccount: wrapMutationAsync(patchAccountMutation.mutateAsync),
    isPatchingAccount: patchAccountMutation.isPending,
    patchAccountError: patchAccountMutation.error,
  }
}
